import React from 'react';
import { ReactTable } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import useAddInsuranceModalContext from '../hooks/useAddInsuranceModalContext';
import { useFormContext } from 'react-hook-form';
import { InsuranceDetailFormValues } from '../../../types/Form';
import { Coverage, InsurancePolicy } from '../../../../../types/AdvisoryReport';
import useCreationFlowReport from '../../../hooks/useCreationFlowReport';
import usePrefillOfferInsurancePolicyTable from '../hooks/usePrefillOfferInsurancePolicyTable';
import { getPolicyModalDefaultValues } from '../shared/modal/AddInsuranceModal';
import { uniqBy } from 'lodash';

interface Props {
  data?: InsurancePolicy[];
  onClose: () => void;
}

const PrefillOfferModalInsurances: React.FC<Props> = ({ data, onClose }) => {
  const { t } = useTranslation();
  const { setInsuranceCompanies, setRiskObjects } =
    useAddInsuranceModalContext();
  const { reset } = useFormContext<InsuranceDetailFormValues>();
  const { data: advisoryReport, isLoading: isLoadingAdvisoryReport } =
    useCreationFlowReport();

  const getDefaultRiskObjects = (fields: Coverage[]) => {
    return uniqBy(
      fields.flatMap(({ risk_object_id, risk_object }) => {
        /** When the coverage has no risk assigned (CMS imports) */
        if (!risk_object && !risk_object_id) {
          return { label: t('OTHER'), value: 'OTHER' };
        }
        if (risk_object && !risk_object_id) {
          return { label: risk_object, value: risk_object };
        }
        return risk_object_id && risk_object
          ? { label: risk_object, value: risk_object_id }
          : [];
      }),
      'value',
    );
  };
  const getDefaultInsuranceCompanies = (fields: Coverage[]) => {
    return uniqBy(
      fields.flatMap(({ insurance_company }) =>
        insurance_company
          ? { label: insurance_company, value: insurance_company }
          : [],
      ),
      'value',
    );
  };

  const handleSelect = (policy: InsurancePolicy) => {
    // handle prefill
    const fields = policy?.coverages || [];
    reset(getPolicyModalDefaultValues(policy));
    setInsuranceCompanies(getDefaultInsuranceCompanies(fields));
    setRiskObjects(getDefaultRiskObjects(fields));
    onClose();
  };

  const { columns } = usePrefillOfferInsurancePolicyTable({
    onSelect: handleSelect,
  });

  return (
    <>
      <ReactTable
        data={
          data || advisoryReport?.insurance_policies.current_insurances || []
        }
        columns={columns}
        isLoading={!data && isLoadingAdvisoryReport}
        emptyText={t('NO-RESULTS-FOUND')}
      />
    </>
  );
};

export default PrefillOfferModalInsurances;
